import { queryOptions, useQuery } from '@tanstack/react-query'
import type { Address } from 'viem'

import { getDefiSdk } from '@/utils/defiSdk'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'

export const rewardsQueryKey = ['rewards'] as const

interface RewardsQueryParams {
  address: Address | undefined
}

export const rewardsQueryOptions = ({ address }: RewardsQueryParams) => {
  return queryOptions({
    queryKey: [...rewardsQueryKey, { address }] as const,
    async queryFn({ queryKey }) {
      const { address } = queryKey[1]

      if (!address) return null
      const defiSdk = getDefiSdk()

      return await defiSdk.getRewardsTransactionsContext({
        path: { userAddress: address },
      })
    },
    select(data) {
      if (data == null) {
        return null
      }
      if (Object.values(data.claimable).length === 0) {
        return null
      }

      return data.claimable
    },
    enabled: !!address,
    retry: 2,
    staleTime: 2 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: true,
  })
}

export const useRewardsQuery = ({
  address,
  initialData,
}: RewardsQueryParams & {
  initialData: ReturnType<typeof rewardsQueryOptions>['initialData']
}) => {
  const { data: rewards, isFetching } = useQuery({
    ...rewardsQueryOptions({ address }),
    initialData,
  })
  return { rewards, isFetching }
}

export type Rewards = ReturnType<typeof useRewardsQuery>['rewards']
